import { assert } from '../assert'

/**
 * The prefix used for Zora universal links to convert web paths to mobile paths.
 * This prefix is used to identify and format URLs that should be opened in the Zora mobile application.
 */
const zoraPrefix = 'zora://'

// TODO(@stephenlaughton): This should be moved to a shared package / use pre-existing package

/**
 * A constant object that maps blockchain names to their respective chain IDs.
 *
 * The object is defined with `as const` to ensure that the values are read-only and
 * cannot be modified. This mapping includes various popular blockchains and their
 * testnets, such as Ethereum, Arbitrum, Optimism, and Zora.
 *
 * @constant
 * @type {Object}
 * @property {number} arbitrum - Chain ID for Arbitrum.
 * @property {number} base - Chain ID for Base.
 * @property {number} baseSepolia - Chain ID for Base Sepolia testnet.
 * @property {number} eth - Chain ID for Ethereum (alias for mainnet).
 * @property {number} ethereum - Chain ID for Ethereum mainnet.
 * @property {number} mainnet - Chain ID for Ethereum mainnet.
 * @property {number} oeth - Chain ID for Optimism (alias for optimism).
 * @property {number} optimism - Chain ID for Optimism.
 * @property {number} sepolia - Chain ID for Sepolia testnet.
 * @property {number} zora - Chain ID for Zora.
 * @property {number} zoraSepolia - Chain ID for Zora Sepolia testnet.
 */
const chainsByChainId = {
  arbitrum: 42161,
  base: 8453,
  baseSepolia: 84532,
  eth: 1,
  ethereum: 1,
  mainnet: 1,
  oeth: 10,
  optimism: 10,
  sepolia: 11155111,
  zora: 7777777,
  zoraSepolia: 999999999,
} as const

export const getZoraPathFromWebPath = (url: string, domain: string) => {
  const [_, webPath] = url.split(domain)
  assert(webPath, 'Invalid web path')
  const path = webPathToMobilePath(webPath)

  console.log('webUrl', url)
  console.log('webPath', webPath)
  console.log('path', path)
  return path
}

/**
 * Converts a web path to a corresponding mobile path.
 *
 * @param webPath - The web path to be converted.
 * @returns The corresponding mobile path.
 *
 * The function performs the following conversions:
 * - If the web path is '/', it converts to the mobile feed path.
 * - If the web path starts with '@', it converts to the mobile profile path.
 * - If the web path starts with 'collect', it converts to the mobile post path.
 * - Otherwise, it prefixes the web path with a predefined mobile path prefix.
 */
export function webPathToMobilePath(webPath: string): string {
  const mobilePath =
    webPath === '/'
      ? webFeedPathToMobile()
      : webPath.startsWith('/@')
        ? webProfilePathToMobile(webPath)
        : webPath.startsWith('/collect')
          ? webPostPathToMobile(webPath)
          : webPath.startsWith('/coin')
            ? webCoinPathToMobile(webPath)
            : `${zoraPrefix}`
  return mobilePath
}

/**
 * Converts a web feed path to a mobile feed path.
 *
 * @returns {string} The mobile feed path.
 */
function webFeedPathToMobile(): string {
  return `${zoraPrefix}`
}

/**
 * Converts a web profile path to a mobile profile path.
 *
 * @param webPath - The web profile path to be converted. It should start with a '/'.
 * @returns The corresponding mobile profile path.
 */
function webProfilePathToMobile(webPath: string): string {
  const [, usernameWithParams] = webPath.split('/')
  assert(usernameWithParams, 'Invalid web path')
  const [username] = usernameWithParams.split('?')
  assert(username, 'Invalid web path')
  return `${zoraPrefix}profile/${username.slice(1)}`
}

/**
 * Converts a web path to a mobile path for a post.
 *
 * @param webPath - The web path to be converted. It should be in the format `/chainId:collectionId/itemId`.
 * @returns The mobile path in the format `collect/{chainIdNumber}/{collectionId}/{itemId}`.
 *
 * @throws Will throw an error if the web path is invalid.
 */
function webPostPathToMobile(webPath: string): string {
  const [, , chainAndCollection, itemIdWithParams] = webPath.split('/')
  assert(chainAndCollection, 'Invalid web path')
  const [chainId, collectionId] = chainAndCollection.split(':')
  assert(chainId, 'Invalid web path')
  const chainIdNumber = chainsByChainId[chainId as keyof typeof chainsByChainId]
  assert(itemIdWithParams, 'Invalid web path')
  const [realItemId] = itemIdWithParams.split('?')
  return `${zoraPrefix}collect/${chainIdNumber}/${collectionId}/${realItemId}`
}

/**
 * Converts a web path to a mobile path for a coin post.
 *
 * Ex:
 *   /coin/base:0xa163d06a74f68d73784527c3d76bb5d77d08fa4d?referrer=0xc583a4b49ca0ed1975691c1b7dd2806942184ddc
 *   -> zora://coin/8453/0xa163d06a74f68d73784527c3d76bb5d77d08fa4d
 *
 * @param webPath - The web path to be converted. It should be in the format `/coin/chainId:collectionId`.
 * @returns The mobile path in the format `coin/{chainIdNumber}/{collectionId}`.
 *
 * @throws Will throw an error if the web path is invalid.
 */
function webCoinPathToMobile(webPath: string): string {
  const [, , chainAndCollectionWithParams] = webPath.split('/')
  assert(chainAndCollectionWithParams, 'Invalid web path')
  const [chainAndCollection] = chainAndCollectionWithParams.split('?')
  assert(chainAndCollection, 'Invalid web path')
  const [chainId, collectionId] = chainAndCollection.split(':')
  const chainIdNumber = chainsByChainId[chainId as keyof typeof chainsByChainId]
  return `${zoraPrefix}coin/${chainIdNumber}/${collectionId}`
}
